/* eslint-disable-next-line import/no-cycle */
import viewTypeConstants from '@/constants/viewTypes';
import store from '../../store';

function hasDealerIdQuery(route) {
  return !!route.query.dealerIds;
}

function hasSearchQuery(route) {
  return !!route.query.query;
}

function hasDashboardDealerIdQuery(route) {
  return !!route.query.dashboardDealerIds;
}

async function isAuctionListV2() {
  const auctionListV2 = await store.dispatch(
    'getFlag',
    store.state.featureFlags.featureFlagSlugs.webMyAcvAuctionListV2,
  );
  return auctionListV2;
}

async function keepDealerIdQueryString(to, from, next) {
  const queryString = { ...to.query };
  if (!hasDealerIdQuery(to) && hasDealerIdQuery(from)) {
    queryString.dealerIds = from.query.dealerIds;
  }

  if (!hasDashboardDealerIdQuery(to) && hasDashboardDealerIdQuery(from)) {
    queryString.dashboardDealerIds = from.query.dashboardDealerIds;
  }

  if (JSON.stringify(to.query) !== JSON.stringify(queryString)) {
    return next({
      ...to,
      query: queryString,
    });
  }

  try {
    if (hasDealerIdQuery(to)) {
      await store.dispatch('auctions/setSelectedDealership', to.query.dealerIds);
    }
    if (hasSearchQuery(to)) {
      await store.dispatch('auctions/setQuery', {
        query: to.query.query,
        moduleKey: to.name,
      });
    }
    if (hasDashboardDealerIdQuery(to)) {
      await store.dispatch('auctions/setSelectedPerformanceDealership', to.query.dashboardDealerIds);
    }
    return next();
  } catch (error) {
    next(false);
    throw new Error(`Navigation Error: ${error.message}`);
  }
}

function checkUserIsLimited(next) {
  if (store.getters.isLimitedUser) {
    next('/auctions/active');
  }
}

async function loadMyInventoryRemoteModule() {
  try {
    const inventoryStoreModule = await import('inventory/inventoryStoreModule');
    if (!store.hasModule('myInventoryRemote')) {
      store.registerModule('myInventoryRemote', inventoryStoreModule.default);
    }
  } catch (error) {
    const formatedError = new Error(error);
    formatedError.message = `MyInventoryV2 - myInventoryRemote module not found - ${error.message}\nFull Error: ${JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}`;
    store.dispatch('analytics/noticeError', {
      error: formatedError,
      component: 'MyAcvRoutes',
      errorString: error.toString(),
    });
  }
}

async function getMyInventoryComponent() {
  const microfrontendEnabled = await store.dispatch(
    'getFlag',
    store.state.featureFlags.featureFlagSlugs.myInventoryUseMicrofrontend,
  );

  return microfrontendEnabled ? import('@/views/MyAcv/MyInventoryV2.vue')
    : import('@/views/MyAcv/MyInventory.vue');
}

const myAcvRoutes = [{
  path: '/myacv',
  component: () => import('@/views/MyAcv/MyAcv.vue'),
  meta: {
    title: 'My ACV',
    shortTitle: 'MyACV',
    linkText: 'My ACV',
  },
  children: [
    {
      path: '',
      name: 'MyACV',
      component: () => import('@/views/MyAcv/MyAcvMenu.vue'),
      async beforeEnter(to, from, next) {
        checkUserIsLimited(next);
        await keepDealerIdQueryString(to, from, next);
      },
      meta: {
        title: 'My ACV',
        shortTitle: 'MyACV',
        linkText: 'My ACV',
      },
    },
    {
      path: 'myinventory',
      name: 'MyAcvMyInventory',
      component: getMyInventoryComponent,
      meta: {
        title: 'My Inventory',
        shortTitle: 'My Inventory',
        description: 'Your inventory feed',
        linkText: '<span class="hidden-xs">My&nbsp;</span>Inventory',
        hideMyACVChat: true,
      },
      async beforeEnter(to, from, next) {
        const microfrontendEnabled = await store.dispatch(
          'getFlag',
          store.state.featureFlags.featureFlagSlugs.myInventoryUseMicrofrontend,
        );

        if (microfrontendEnabled) {
          await loadMyInventoryRemoteModule();
        }
        await keepDealerIdQueryString(to, from, next);
        const routeEnabled = await store.dispatch(
          'getFlag',
          store.state.featureFlags.featureFlagSlugs.myInventoryEnabled,
        );
        if (routeEnabled) {
          next();
        } else {
          next({ name: 'MyAcvActiveBuying' });
        }
      },
    },
    {
      path: 'saved',
      name: 'MyAcvSaved',
      component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
      async beforeEnter(to, from, next) {
        checkUserIsLimited(next);
        await keepDealerIdQueryString(to, from, next);
      },
      meta: {
        title: 'Saved Auctions',
        shortTitle: 'Saved',
        description: 'Auctions you\'ve saved',
        linkText: 'Saved<span class="hidden-xs">&nbsp;Auctions</span>',
        sellerOnly: true,
        hasExport: true,
      },
    },
    {
      path: 'request-inspection',
      name: 'MyAcvRequestInspectionParent',
      component: () => import('@/views/MyAcv/RequestInspection.vue'),
      redirect: '/myacv/request-inspection/new',
      async beforeEnter(to, from, next) {
        await keepDealerIdQueryString(to, from, next);
        const routeEnabled = await store.dispatch(
          'getFlag',
          store.state.featureFlags.featureFlagSlugs.showDigitalRunSheet,
        );
        if (routeEnabled) {
          next();
        } else {
          next({ name: 'MyAcvActiveBuying' });
        }
      },
      meta: {
        title: 'Request Inspection',
        shortTitle: 'Request Inspection',
        description: 'Request an Inspection to Sell Your Vehicle',
        linkText: '<span class="hidden-xs">Request&nbsp;</span>Inspection',
        sellerOnly: true,
        hasExport: false,
        divider: true,
      },
      children: [
        {
          path: 'new',
          name: 'MyAcvRequestInspection',
          meta: {
            title: 'New',
            shortTitle: 'New',
            description: 'Request a New Inspection to Sell Your Vehicle',
            linkText: 'New',
            viewType: 'new',
            sellerOnly: true,
          },
        },
        {
          path: 'requested',
          name: 'MyAcvRequestedInspections',
          meta: {
            title: 'Requested',
            shortTitle: 'Requested',
            description: 'Submitted Inspection Requests',
            linkText: 'Requested',
            viewType: 'requested',
            sellerOnly: true,
          },
        },
      ],
    },
    {
      path: 'active',
      name: 'MyAcvActive',
      component: async () => (await isAuctionListV2()
        ? import('@/views/MyAcv/MyAcvAuctionListV2.vue')
        : import('@/views/MyAcv/MyAcvAuctionList.vue')),
      redirect: '/myacv/active/buying',
      meta: {
        title: 'My ACV',
        shortTitle: 'Active',
        description: 'Auctions currently buying or selling',
        linkText: 'Active<span class="hidden-xs">&nbsp;Auctions</span>',
      },
      children: [
        {
          path: 'buying',
          name: 'MyAcvActiveBuying',
          meta: {
            title: 'Active Buying',
            shortTitle: 'Buying',
            noAuctionsDisplayName: 'Active Buying',
            noAuctionsHeader: 'No Active Buying Auctions',
            noAuctionsMessage: 'You don\'t currently have any active buying auctions. When you do, they\'ll appear here.',
            linkText: 'Buying',
            allowAuctionCommonModals: true,
            endpointContext: 'activeBuying',
          },
          async beforeEnter(to, from, next) {
            checkUserIsLimited(next);
            await keepDealerIdQueryString(to, from, next);
          },
        },
        {
          path: 'selling',
          name: 'MyAcvActiveSelling',
          async beforeEnter(to, from, next) {
            checkUserIsLimited(next);
            await keepDealerIdQueryString(to, from, next);
          },
          meta: {
            title: 'Active Selling',
            shortTitle: 'Selling',
            noAuctionsDisplayName: 'Active Selling',
            noAuctionsHeader: 'No Active Selling Auctions',
            noAuctionsMessage: 'You don\'t currently have any active selling auctions. When you do, they\'ll appear here.',
            linkText: 'Selling',
            sellerOnly: true,
            allowAuctionCommonModals: true,
            endpointContext: 'activeSelling',
          },
        },
      ],
    },
    {
      path: 'pendingproxy',
      name: 'MyAcvPendingProxy',
      component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
      async beforeEnter(to, from, next) {
        checkUserIsLimited(next);
        await keepDealerIdQueryString(to, from, next);
      },
      meta: {
        title: 'Pending Proxy',
        shortTitle: 'Pending Proxy',
        description: 'Auctions with a persistent proxy placed',
        noAuctionsDisplayName: 'Pending Proxy',
        noAuctionsHeader: 'No Active Pending Proxy Bids',
        noAuctionsMessage: 'You don\'t currently have any active pending proxy bids. When you do, they\'ll appear here.',
        linkText: 'Pending<span class="hidden-xs">&nbsp;Proxy</span>',
        allowAuctionCommonModals: true,
      },
    },
    {
      path: 'negotiations',
      name: 'MyAcvNegotiations',
      component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
      redirect: '/myacv/negotiations/buying',
      meta: {
        title: 'In Negotiation',
        shortTitle: 'In Negotiation',
        description: 'Auctions that have a pending offer or are in active negotiation',
        linkText: 'In Negotiation',
      },
      children: [
        {
          path: 'buying',
          name: 'MyAcvNegotiationsBuying',
          meta: {
            title: 'In Negotiation - Buying',
            shortTitle: 'Buying',
            noAuctionsDisplayName: 'Active Buying Negotiations',
            noAuctionsHeader: 'No Active Buying Negotiations ',
            noAuctionsMessage: 'You don\'t currently have any active buying offers or negotiations. When you do, they\'ll appear here.',
            linkText: 'Buying',
          },
          async beforeEnter(to, from, next) {
            checkUserIsLimited(next);
            await keepDealerIdQueryString(to, from, next);
          },
        },
        {
          path: 'selling',
          name: 'MyAcvNegotiationsSelling',
          meta: {
            title: 'In Negotiation - Selling',
            shortTitle: 'Selling',
            noAuctionsDisplayName: 'Active Selling Negotiations',
            noAuctionsHeader: 'No Active Selling Negotiations',
            noAuctionsMessage: 'You don\'t currently have any active selling offers or negotiations. When you do, they\'ll appear here.',
            linkText: 'Selling',
            sellerOnly: true,
          },
          async beforeEnter(to, from, next) {
            checkUserIsLimited(next);
            await keepDealerIdQueryString(to, from, next);
          },
        },
      ],
    },
    {
      path: 'offers',
      name: 'MyAcvMakeOffer',
      redirect: '/myacv/offers/received',
      component: () => import('@/views/MyAcv/Offers/MyAcvActionableOffers.vue'),
      async beforeEnter(to, from, next) {
        checkUserIsLimited(next);
        await keepDealerIdQueryString(to, from, next);
        next();
      },
      meta: {
        title: 'Offers',
        shortTitle: 'Offers',
        description: 'Pending Selling offers',
        noAuctionsDisplayName: 'Active Offers',
        noAuctionsHeader: 'No Active Offers',
        noAuctionsMessage: 'You don\'t currently have any active selling offers. When you do, they\'ll appear here.',
        linkText: 'Offers',
        hasExport: false,
        divider: false,
      },
      children: [
        {
          path: 'received',
          name: 'MyAcvReceivedOffers',
          component: () => import('@/views/MyAcv/Offers/ReceivedOffers.vue'),
          async beforeEnter(to, from, next) {
            // this is to route to buying when the offers tab is clicked in the navigation drawer
            const showOffersPageForBuyers = await store.dispatch(
              'getFlag',
              store.state.featureFlags.featureFlagSlugs.showOffersPageForBuyers,
            );

            if (showOffersPageForBuyers) {
              return next({ name: 'BuyerOffers' });
            }
            return next();
          },
          props: {
            viewType: viewTypeConstants.SELLING,
          },
          meta: {
            title: 'Offers',
            hideFromView: true,
          },
        },
        {
          path: 'received/buying',
          name: 'BuyerOffers',
          component: () => import('@/views/MyAcv/Offers/BuyerOffers.vue'),
          props: {
            viewType: viewTypeConstants.BUYING,
          },
          meta: {
            title: 'Buyer Offers',
            hideFromView: true,
          },
        },
        {
          path: 'received/selling',
          name: 'SellerOffers',
          component: () => import('@/views/MyAcv/Offers/SellerOffers.vue'),
          props: {
            viewType: viewTypeConstants.SELLING,
          },
          meta: {
            title: 'Seller Offers',
            hideFromView: true,
          },
        },
      ],
    },
    {
      path: 'bidmatrix',
      name: 'MyAcvBidMatrix',
      component: () => import('@/views/MyAcv/MyAcvBidMatrix.vue'),
      meta: {
        title: 'S.A.M. Buying Rules',
        shortTitle: 'S.A.M. Buying Rules',
        description: 'S.A.M. Buying Rules',
        linkText: 'S.A.M. Buying Rules',
        divider: true,
      },
    },
    {
      path: 'billing',
      name: 'MyAcvBilling',
      component: () => import('@/views/MyAcv/MyAcvBilling.vue'),
      redirect: '/myacv/billing/invoices',
      beforeEnter(to, from, next) {
        checkUserIsLimited(next);
        if (
          store.getters['myAcvInvoices/isInvoicesMenuEnabled']
        ) {
          next();
        } else {
          next({ name: 'MyAcvSold' });
        }
      },
      meta: {
        title: 'Billing',
        shortTitle: 'Billing',
        description: 'Billing',
        linkText: 'Billing',
        badge: 'New',
      },
      children: [
        {
          path: 'invoices',
          name: 'MyAcvBillingInvoices',
          component: () => import('@/views/MyAcv/Invoices/MyAcvInvoices.vue'),
          redirect: '/myacv/billing/invoices/open',
          meta: {
            title: 'Invoices',
            shortTitle: 'Invoices',
            linkText: 'Invoices',
            alwaysShow: true,
          },
          children: [
            {
              path: 'open',
              name: 'MyAcvOpenInvoices',
              component: () => import('@/views/MyAcv/Invoices/OpenInvoices.vue'),
              props: {
                viewType: 'open',
              },
              meta: {
                title: 'Open Invoices',
                hideFromView: true,
              },
            },
            {
              path: 'closed',
              name: 'MyAcvClosedInvoices',
              component: () => import('@/views/MyAcv/Invoices/ClosedInvoices.vue'),
              props: {
                viewType: 'closed',
              },
              meta: {
                title: 'Closed Invoices',
                hideFromView: true,
              },
            },
          ],
        },
      ],
    },
    {
      path: 'won',
      name: 'MyAcvWon',
      component: () => import('@/views/MyAcv/MyAcvWonAuctions.vue'),
      async beforeEnter(to, from, next) {
        checkUserIsLimited(next);
        await keepDealerIdQueryString(to, from, next);
      },
      meta: {
        title: 'Won Auctions',
        shortTitle: 'Won',
        description: 'Auctions you\'ve previously won',
        linkText: 'Won<span class="hidden-xs">&nbsp;Auctions</span>',
        hasExport: true,
      },
    },
    {
      path: 'sold',
      name: 'MyAcvSold',
      component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
      async beforeEnter(to, from, next) {
        checkUserIsLimited(next);
        await keepDealerIdQueryString(to, from, next);
      },
      meta: {
        title: 'Sold Auctions',
        shortTitle: 'Sold',
        description: 'Auctions you\'ve previously won',
        linkText: 'Sold<span class="hidden-xs">&nbsp;Auctions</span>',
        hasExport: true,
        sellerOnly: true,
      },
    },
    {
      path: 'titles/:tab?',
      name: 'MyAcvTitleHub',
      component: () => import('@/views/MyAcv/MyAcvTitleHub.vue'),
      async  beforeEnter(to, from, next) {
        const titlesRedesign = await store.dispatch(
          'getFlag',
          store.state.featureFlags.featureFlagSlugs.urgentTitleRedesign,
        );

        if (titlesRedesign) {
          next();
        } else {
          // if feature flag is not enabled, redirect to old title hub
          next({ name: 'MyAcvDigitalClerking', params: { viewType: 'open' } });
        }
      },
      meta: {
        validTabs: ['urgent', 'due-soon', 'recently-sold', 'closed'],
        shortTitle: 'Title Hub',
        logoAlt: 'Title Hub Logo',
        title: 'Title Hub',
        linkText: 'Title Hub',
        description: 'Manage open and closed titles',
        sellerOnly: true,
        hasExport: false,
        divider: true,
      },
    },
    {
      path: 'performance',
      name: 'MyAcvSellerPerformance',
      component: () => import('@/views/MyAcv/MyAcvSellerPerformance.vue'),
      async beforeEnter(to, from, next) {
        await keepDealerIdQueryString(to, from, next);
        const routeEnabled = await store.dispatch(
          'getFlag',
          store.state.featureFlags.featureFlagSlugs.showSellerDashboard,
        );
        if (routeEnabled) {
          next();
        } else {
          next({ name: 'MyAcvActiveBuying' });
        }
      },
      meta: {
        title: 'Performance',
        shortTitle: 'Performance',
        description: 'Seller Performance Dashboard',
        linkText: 'Performance',
        sellerOnly: true,
      },
    },
    {
      path: 'fee-schedule',
      name: 'MyAcvFeeSchedule',
      component: () => import('@/views/MyAcv/FeeSchedule/MyAcvFeeSchedule.vue'),
      meta: {
        title: 'Fee Schedule',
        shortTitle: 'Fee Schedule',
        description: 'Fee Schedule information',
        linkText: 'Fee Schedule',
        hideSidebar: true,
      },
      async beforeEnter(to, from, next) {
        const routeEnabled = await store.dispatch(
          'getFlag',
          store.state.featureFlags.featureFlagSlugs.enableNewFeeSchedulePage,
        );
        if (routeEnabled) {
          next();
        } else {
          next({ path: '/myacv' });
        }
      },
    },
  ],
}];

export {
  keepDealerIdQueryString,
  myAcvRoutes,
};
